// Batas kirim manual Tokopedia (field ship_by_date_iso): chip tanggal WIB + editor untuk Tim Mockup Tokopedia / Owner / Admin.
import axios from "axios";
import { useEffect, useState } from "react";
import { CalendarClock, Lock } from "lucide-react";
import { toast } from "sonner";
import { useChannel } from "./channel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tokopedia/deadline`;
const TTL = 30000;
const store = {};
const subs = new Set();
const emit = () => subs.forEach(f => f());

const fetchOne = (num) => {
  const e = store[num];
  if (e && (e.p || Date.now() - e.at < TTL)) return;
  store[num] = { ...(e || {}), p: axios.get(`${API}/${encodeURIComponent(num)}`, { withCredentials: true })
    .then(r => { store[num] = { data: r.data, at: Date.now() }; })
    .catch(() => { store[num] = { data: e?.data || null, at: Date.now() }; })
    .finally(emit) };
};

export const wibDay = (v) => {
  const t = v instanceof Date ? v : v ? new Date(v) : null;
  return t && !isNaN(t) ? t.toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }) : "";
};

export function dayLevel(iso, now = Date.now()) {
  const d = wibDay(iso);
  if (!d) return "none";
  const today = wibDay(new Date(now)), tmr = wibDay(new Date(now + 86400000));
  return d < today ? "late" : d === today ? "today" : d === tmr ? "tomorrow" : "later";
}

const LABEL = { late: "Terlambat", today: "Kirim Hari Ini", tomorrow: "Kirim Besok", none: "Batas kirim belum diisi" };
const wibTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }) : "";
const fmtDay = (iso) => new Date(iso).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric" }) +
  (wibTime(iso) && wibTime(iso) !== "23:59" ? ` ${wibTime(iso)} WIB` : "");
const fmtAt = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) + " WIB" : "";
const stop = (e) => e.stopPropagation();

function useDeadline(num, on) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on || !num) return undefined;
    const f = () => tick(x => x + 1);
    subs.add(f); fetchOne(num);
    return () => { subs.delete(f); };
  }, [num, on]);
  return store[num]?.data || null;
}

function Editor({ d, num, onDone }) {
  const [date, setDate] = useState(wibDay(d.deadline_iso));
  const [time, setTime] = useState(wibTime(d.deadline_iso));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/${encodeURIComponent(num)}`, { date, time, reason }, { withCredentials: true });
      store[num] = { data: r.data, at: Date.now() }; emit();
      window.dispatchEvent(new CustomEvent("tkp-deadline-changed", { detail: { order_number: num } }));
      toast.success(`Batas kirim ${num} disimpan`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan batas kirim");
    } finally { setBusy(false); }
  };
  return <span className="tkp-dl-edit" data-testid={`tkp-dl-editor-${num}`}>
    <input type="date" value={date} onChange={e => setDate(e.target.value)} aria-label="Tanggal batas kirim" data-testid={`tkp-dl-date-${num}`}/>
    <input type="time" value={time} onChange={e => setTime(e.target.value)} aria-label="Jam batas kirim (WIB)" data-testid={`tkp-dl-time-${num}`}/>
    {d.deadline_iso && <input type="text" value={reason} maxLength={300} onChange={e => setReason(e.target.value)}
      placeholder="Alasan ubah (opsional)" data-testid={`tkp-dl-reason-${num}`}/>}
    <small>Jam kosong = 23:59 WIB</small>
    <span className="tkp-dl-acts">
      <button type="button" className="tkp-main" disabled={!date || busy} onClick={save} data-testid={`tkp-dl-save-${num}`}>{busy ? "Menyimpan…" : "Simpan"}</button>
      <button type="button" onClick={onDone} data-testid={`tkp-dl-cancel-${num}`}>Batal</button>
    </span>
  </span>;
}

export function TokopediaDeadline({ orderNumber }) {
  const on = useChannel() === "tokopedia";
  const d = useDeadline(orderNumber, on);
  const [open, setOpen] = useState(false);
  if (!on || !d) return null;
  const lv = dayLevel(d.deadline_iso);
  const text = lv === "later" ? `Kirim ${fmtDay(d.deadline_iso)}` : lv === "none" ? LABEL.none : `${LABEL[lv]} · ${fmtDay(d.deadline_iso)}`;
  const who = d.deadline_by ? `Diatur ${d.deadline_by} · ${fmtAt(d.deadline_set_at)}` : "";
  return <span className="tkp-dl" onClick={stop} onKeyDown={stop} data-testid={`tkp-dl-${orderNumber}`}>
    <span className={`tkp-dl-chip is-${lv}`} title={who || undefined} data-level={lv} data-testid={`tkp-dl-chip-${orderNumber}`}>
      <CalendarClock size={11} aria-hidden="true"/> {text}
    </span>
    {d.locked ? <small className="tkp-dl-who" data-testid={`tkp-dl-locked-${orderNumber}`}><Lock size={10} aria-hidden="true"/> Otomatis dari Tokopedia</small>
      : who && <small className="tkp-dl-who" data-testid={`tkp-dl-who-${orderNumber}`}>{who}</small>}
    {d.can_edit && !open && <button type="button" className="tkp-dl-btn" onClick={() => setOpen(true)} data-testid={`tkp-dl-open-${orderNumber}`}>
      {d.deadline_iso ? "Ubah Batas Kirim" : "Atur Batas Kirim"}</button>}
    {open && <Editor d={d} num={orderNumber} onDone={() => setOpen(false)}/>}
  </span>;
}
